import React from 'react';
import { Button } from './ui/button';
import {
  FEATURE_MATRIX,
  TRIAL_DAYS,
  displayPlansForCurrency,
  featureCellValue,
  formatPlanPrice,
} from '../lib/pricingCatalog';

function PlanCard({
  title,
  price,
  subtitle,
  features,
  selected,
  onClick,
  badge,
  cta,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition-colors ${
        selected
          ? 'border-brand-500 bg-brand-50'
          : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-zinc-900">{title}</p>
          <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="text-right">
          {badge ? (
            <span className="inline-block mb-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-50 text-brand-700 border border-brand-200">
              {badge}
            </span>
          ) : null}
          <p className="text-sm font-semibold text-zinc-900 whitespace-nowrap">{price}</p>
        </div>
      </div>
      {features?.length ? (
        <ul className="mt-3 space-y-1 text-xs text-zinc-600">
          {features.slice(0, 4).map((f) => (
            <li key={f}>• {f}</li>
          ))}
        </ul>
      ) : null}
      {cta ? <p className="mt-2 text-xs font-medium text-brand-700">{cta}</p> : null}
    </button>
  );
}

export default function PlanSelector({
  currency,
  selectedPlan,
  selectedCycle,
  onSelectPlan,
  apiPlans = [],
  compact = false,
  showFeatureMatrix = false,
}) {
  const plans = displayPlansForCurrency(currency, apiPlans);
  const trial = plans.find((p) => p.slug === 'trial');
  const enterprise = plans.find((p) => p.slug === 'enterprise');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs tracking-[0.1em] uppercase font-medium text-zinc-400">
          Choose a plan
        </p>
      </div>

      <div className={compact ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 gap-3'}>
        <PlanCard
          title="Free trial"
          badge={`${TRIAL_DAYS} days`}
          price={formatPlanPrice(0, currency)}
          subtitle={`Full platform access for ${TRIAL_DAYS} days`}
          features={trial?.features}
          selected={selectedPlan === 'free' && selectedCycle === 'trial'}
          onClick={() => onSelectPlan('free', 'trial')}
          cta="Start free trial"
        />

        <button
          type="button"
          onClick={() => onSelectPlan('enterprise', 'custom')}
          className={`w-full text-left p-4 rounded-lg border transition-colors ${
            selectedPlan === 'enterprise'
              ? 'border-brand-500 bg-brand-50'
              : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-zinc-900">Enterprise</p>
              <p className="text-xs text-zinc-500 mt-0.5">On-site deployment & custom SLA</p>
            </div>
            <p className="text-sm font-semibold text-zinc-900">Custom</p>
          </div>
          {enterprise?.features?.length ? (
            <ul className="mt-3 space-y-1 text-xs text-zinc-600">
              {enterprise.features.slice(0, 4).map((f) => (
                <li key={f}>• {f}</li>
              ))}
            </ul>
          ) : null}
        </button>
      </div>

      {showFeatureMatrix && !compact ? (
        <div className="mt-6 overflow-x-auto rounded-lg border border-zinc-200">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="text-left p-3 font-semibold">Feature</th>
                <th className="text-left p-3 font-semibold">Trial</th>
                <th className="text-left p-3 font-semibold">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {FEATURE_MATRIX.map((row) => (
                <tr key={row.key} className="border-t border-zinc-100">
                  <td className="p-3 text-zinc-700">{row.label}</td>
                  <td className="p-3 text-zinc-600">
                    {featureCellValue(trial?.featurePackaging, row.key)}
                  </td>
                  <td className="p-3 text-zinc-600">
                    {featureCellValue(enterprise?.featurePackaging, row.key)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {compact ? (
        <p className="text-xs text-zinc-500 text-center">
          <Button type="button" variant="link" className="h-auto p-0 text-xs" onClick={() => window.location.href = `/pricing?currency=${currency}`}>
            Compare trial and Enterprise
          </Button>
        </p>
      ) : null}
    </div>
  );
}
